import { computed, ref } from 'vue'
import { apiFetch } from './api'

export type ActiveRunRow = {
  id: string
  account_id: string
  agent_id: string | null
  sales_play_id: string | null
  status: string
}

const pendingReachouts = ref(0)
const runningAgentRuns = ref(0)
const queuedAgentRuns = ref(0)
const pendingHumanDials = ref(0)
const activeRuns = ref([])
const deployVersion = ref<string | null>(null)
const lastUpdatedAt = ref(null)

let bootVersion: string | null = null
const newVersionAvailable = ref<string | null>(null)

let pollTimer = null
const POLL_INTERVAL_MS = 5000
const activeRunListeners = new Set<({}) => void>()

function _normalizeCount(v) {
  const n = Number(v || 0)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.floor(n)
}

function _normalizeActiveRuns(rows): ActiveRunRow[] {
  const source = Array.isArray(rows) ? rows : []
  return source.map((row) => ({
    id: String(row?.id || ''),
    account_id: row?.account_id ? String(row.account_id) : '',
    agent_id: row?.agent_id ? String(row.agent_id) : null,
    sales_play_id: row?.sales_play_id ? String(row.sales_play_id) : null,
    status: String(row?.status || ''),
  }))
}

function _activeRunsSignature(rows) {
  return JSON.stringify((rows || []).map((row) => ({
    id: row.id,
    account_id: row.account_id,
    agent_id: row.agent_id,
    sales_play_id: row.sales_play_id,
    status: row.status,
  })))
}

function _computeActiveRunDelta(nextValue: { id: string; account_id: string; agent_id: string; status: string }[], prevValue: any[]) {
  const nextMap = new Map((nextValue || []).map((row) => [row.id, row]))
  const prevMap = new Map((prevValue || []).map((row) => [row.id, row]))
  const started = []
  const updated = []
  const finished = []

  for (const [id, nextRow] of nextMap.entries()) {
    const prevRow = prevMap.get(id)
    if (!prevRow) {
      started.push(nextRow)
      continue
    }
    if (
      prevRow.account_id !== nextRow.account_id ||
      prevRow.agent_id !== nextRow.agent_id ||
      prevRow.status !== nextRow.status
    ) {
      updated.push({ previous: prevRow, current: nextRow })
    }
  }

  for (const [id, prevRow] of prevMap.entries()) {
    if (!nextMap.has(id)) {
      finished.push(prevRow)
    }
  }

  return {
    started,
    updated,
    finished,
  }
}

function _notifyActiveRunListeners(nextValue: { id: string; account_id: string; agent_id: string; status: string }[], prevValue: any[], delta: { started: any[]; updated: any[]; finished: any[] }) {
  for (const listener of activeRunListeners) {
    listener({
      current: nextValue,
      previous: prevValue,
      delta,
    })
  }
}

async function refresh({ silent = true } = {}) {
  try {
    const resp = await apiFetch('/action-counts')
    pendingReachouts.value = _normalizeCount(resp?.pending_reachouts)
    runningAgentRuns.value = _normalizeCount(resp?.running_agent_runs)
    queuedAgentRuns.value = _normalizeCount(resp?.queued_agent_runs)
    pendingHumanDials.value = _normalizeCount(resp?.pending_human_dials)
    deployVersion.value = resp.version
    // Remind the user to reload their browser if a new version was 
    // released while they have this tab open.
    if (resp.version) {
      if (bootVersion === null) {
        bootVersion = resp.version
      } else if (resp.version !== bootVersion && !newVersionAvailable.value) {
        newVersionAvailable.value = resp.version
      }
    }
    const prevActiveRuns = activeRuns.value
    const nextActiveRuns = _normalizeActiveRuns(resp?.active_runs)
    activeRuns.value = nextActiveRuns
    if (_activeRunsSignature(prevActiveRuns) !== _activeRunsSignature(nextActiveRuns)) {
      const delta = _computeActiveRunDelta(nextActiveRuns, prevActiveRuns)
      _notifyActiveRunListeners(nextActiveRuns, prevActiveRuns, delta)
    }
    lastUpdatedAt.value = new Date().toISOString()
    return {
      pending_reachouts: pendingReachouts.value,
      running_agent_runs: runningAgentRuns.value,
      queued_agent_runs: queuedAgentRuns.value,
      pending_human_dials: pendingHumanDials.value,
      active_runs: nextActiveRuns,
    }
  } catch (e) {
    if (!silent) throw e
    return null
  }
}

function subscribeActiveRuns(listener: { ({ }: {}): void; ({ }: {}): void }) {
  if (typeof listener !== 'function') return () => {}
  activeRunListeners.add(listener)
  return () => {
    activeRunListeners.delete(listener)
  }
}

function startPolling() {
  if (pollTimer) return
  refresh({ silent: true })
  pollTimer = setInterval(() => {
    if (document.visibilityState !== 'visible') return
    refresh({ silent: true })
  }, POLL_INTERVAL_MS)
}

function stopPolling() {
  if (!pollTimer) return
  clearInterval(pollTimer)
  pollTimer = null
}

export function runningAgentCountForPlay(salesPlayId: string | null | undefined) {
  return computed(() => {
    const target = String(salesPlayId || '').trim()
    if (!target) return 0
    let n = 0
    for (const row of activeRuns.value as ActiveRunRow[]) {
      if (row.sales_play_id === target && row.status === 'running') n += 1
    }
    return n
  })
}

export function useActionCounts() {
  return {
    pendingReachouts,
    runningAgentRuns,
    queuedAgentRuns,
    pendingHumanDials,
    activeRuns,
    deployVersion,
    newVersionAvailable,
    lastUpdatedAt,
    refresh,
    subscribeActiveRuns,
    startPolling,
    stopPolling,
  }
}
